import { getSupabase } from "./supabase";

import { createTelemetryQueue } from "./telemetryQueue.mjs";

/**
 * MER-208: fire-and-forget dashboard usage telemetry. Events queue in
 * memory and flush every 5 seconds as ONE multi-row insert into
 * dashboard_event (migration 0040). A Supabase outage, RLS rejection,
 * or thrown error never reaches the UI: every external call is
 * swallowed.
 *
 * Identity rule (documented on the MER-208 plan comment): events
 * carry the session user's tenant from their tenant_members row,
 * resolved once per session. Users WITHOUT a membership row (staff,
 * including view-as impersonation) record nothing: MER-211's usage
 * numbers measure customer engagement, not staff browsing. This
 * mirrors the table's INSERT RLS, which would reject those rows
 * anyway.
 *
 * occurred_at is stamped client-side at track() time: a batched
 * insert evaluates server now() once for the whole batch, which would
 * give identical timestamps and destroy within-batch ordering.
 */

export type TelemetryEventType = "login" | "page_view" | "tab_change" | "widget_drill" | "sign_out";

interface Identity {
  tenantId: string;
  userId: string;
}

const queue = createTelemetryQueue();

// null = resolved as "telemetry off" (no session or no membership).
// undefined = not resolved yet. Reset via _resetTelemetryForTests.
let identity: Identity | null | undefined;
let identityPromise: Promise<Identity | null> | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

async function resolveIdentity(): Promise<Identity | null> {
  try {
    const supabase = getSupabase();
    const { data } = await supabase.auth.getSession();
    const userId = data.session?.user?.id;
    if (!userId) return null;
    const { data: rows, error } = await supabase
      .from("tenant_members")
      .select("tenant_id")
      .eq("user_id", userId)
      .limit(1);
    if (error) return null;
    const tenantId = (rows as Array<{ tenant_id: string }> | null)?.[0]?.tenant_id;
    return tenantId ? { tenantId, userId } : null;
  } catch {
    return null;
  }
}

async function getIdentity(): Promise<Identity | null> {
  if (identity !== undefined) return identity;
  identityPromise = identityPromise ?? resolveIdentity();
  identity = await identityPromise;
  identityPromise = null;
  return identity;
}

async function flush(): Promise<void> {
  const batch = queue.drainAll();
  if (batch.length === 0) return;
  try {
    const id = await getIdentity();
    if (!id) return; // telemetry off: drop silently
    const supabase = getSupabase();
    await supabase.from("dashboard_event").insert(
      batch.map((e) => ({
        tenant_id: id.tenantId,
        user_id: id.userId,
        event_type: e.event_type,
        payload: e.payload,
        occurred_at: e.occurred_at,
      })),
    );
  } catch {
    // Fire-and-forget contract: never surface telemetry failures.
  }
}

/**
 * Record one event. Synchronous and exception-free; the insert
 * happens on the batch flush.
 */
export function track(eventType: TelemetryEventType, payload: Record<string, unknown> = {}): void {
  try {
    const now = Date.now();
    const armed = queue.enqueue(
      { event_type: eventType, payload, occurred_at: new Date(now).toISOString() },
      now,
    );
    if (armed !== null && flushTimer === null) {
      flushTimer = setTimeout(() => {
        flushTimer = null;
        void flush();
      }, armed - now);
    }
  } catch {
    // Never let telemetry break the UI thread.
  }
}

/**
 * Bounded immediate flush for the sign-out path: the JWT dies when
 * signOut resolves, so pending events (including the sign_out event
 * itself) must land first. Resolves within timeoutMs regardless.
 */
export async function flushNow(timeoutMs = 1500): Promise<void> {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  try {
    await Promise.race([flush(), new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))]);
  } catch {
    // Fire-and-forget contract holds here too.
  }
}

/** Session ended: next track() re-resolves identity. */
export function resetTelemetryIdentity(): void {
  identity = undefined;
  identityPromise = null;
}

export function _resetTelemetryForTests(): void {
  resetTelemetryIdentity();
  queue.drainAll();
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}
